import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, primeCsrf } from './api';
import type { Paginated } from './paginated';
import type { PortalApprovableData } from '@/types/api';

/**
 * Portal proposals. Proposals are Approvables, scoped and permission-gated
 * server-side, and can be accepted, declined, or deferred ("Maybe later").
 * Invoices are first-class and live in portal-invoices.ts.
 */

export function usePortalProposals() {
  return useQuery({
    queryKey: ['portal-proposals'],
    queryFn: async (): Promise<Paginated<PortalApprovableData>> => {
      const { data } = await api.get<Paginated<PortalApprovableData>>('/v1/portal/proposals');
      return data;
    },
  });
}

export function usePortalApprovable(id: number | undefined) {
  return useQuery({
    queryKey: ['portal-approvable', id],
    enabled: id != null,
    queryFn: async (): Promise<PortalApprovableData> => {
      const { data } = await api.get<PortalApprovableData>(`/v1/portal/approvables/${id}`);
      return data;
    },
  });
}

function useApprovableAction(id: number, action: 'approve' | 'decline' | 'defer') {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body?: { comment?: string }): Promise<PortalApprovableData> => {
      await primeCsrf();
      const { data } = await api.post<PortalApprovableData>(
        `/v1/portal/approvables/${id}/${action}`,
        body ?? {},
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['portal-approvable', id] });
      qc.invalidateQueries({ queryKey: ['portal-proposals'] });
    },
  });
}

export const useApproveProposal = (id: number) => useApprovableAction(id, 'approve');
export const useDeclineProposal = (id: number) => useApprovableAction(id, 'decline');
export const useDeferProposal = (id: number) => useApprovableAction(id, 'defer');
